/**
 * Adaptadores de infraestructura para sincronización de Customer.
 * Contiene llamadas directas a Supabase (infraestructura).
 */
import { supabase } from '../../shared/supabase/supabaseClient';
import type { RemoteCustomerRow, RemoteCustomerMovementRow, RemoteSalePaymentAllocationRow } from './customerPullDomain';

export type CustomersSnapshotPayload = {
  customers: RemoteCustomerRow[];
  customer_movements: RemoteCustomerMovementRow[];
  sale_payment_allocations: RemoteSalePaymentAllocationRow[];
  snapshot_complete?: boolean;
  has_changes?: boolean;
  schema_version?: number;
  server_cursor?: string | null;
};

/**
 * Snapshot de customer (clientes, movimientos, allocations).
 * @param sinceIso null = full; ISO timestamp = incremental (customers updated_at, movements/allocations created_at).
 */
// Sin timeout el request podía colgar para siempre en staging/redes lentas y
// la pantalla de Clientes quedaba en "Cargando clientes…" sin forma de
// reintentar (el pane sí tiene error path, pero el promise nunca settleaba).
const SNAPSHOT_TIMEOUT_MS = 30_000;

export async function fetchRemoteCustomersSnapshot(sinceIso?: string | null): Promise<CustomersSnapshotPayload> {
  const { data, error } = await Promise.race([
    supabase.rpc('get_mobile_customers_snapshot', {
      p_since: sinceIso ?? null,
    }),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('customers snapshot timeout')), SNAPSHOT_TIMEOUT_MS),
    ),
  ]);

  if (error) {
    throw error;
  }

  if (!data || typeof data !== 'object') {
    return {
      customers: [],
      customer_movements: [],
      sale_payment_allocations: [],
      snapshot_complete: true,
    };
  }

  const snapshot = data as CustomersSnapshotPayload & {
    snapshot_complete?: boolean;
    has_changes?: boolean;
    schema_version?: number;
    server_cursor?: string | null;
  };
  return {
    customers: Array.isArray(snapshot.customers) ? snapshot.customers : [],
    customer_movements: Array.isArray(snapshot.customer_movements) ? snapshot.customer_movements : [],
    sale_payment_allocations: Array.isArray(snapshot.sale_payment_allocations)
      ? snapshot.sale_payment_allocations
      : [],
    // Strict: incomplete incremental must not be treated as full list.
    snapshot_complete: snapshot.snapshot_complete === true,
    has_changes: snapshot.has_changes,
    schema_version: snapshot.schema_version,
    server_cursor: snapshot.server_cursor ?? null,
  };
}
